import React from 'react';
import PropTypes from 'prop-types';

import {Box, Button, IconButton, Stack, TextField, Typography} from 'views/components';
import {ArrowForwardIos, ChevronLeft, InfoOutlined} from 'views/icons';
import {Translate, translate} from 'views/module';

const ScheduleEventEditFlexible = (props) => {
  const {initialEvent, onSave, onRemove} = props;

  const [preferredDays, setPreferredDays] = React.useState([]);
  const [preferredTimes, setPreferredTimes] = React.useState([]);
  const [visitDuration, setVisitDuration] = React.useState('');

  const daysOfWeek = React.useMemo(() => ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'], []);
  const dayOptions = React.useMemo(() => ([
    {id: 'ANY_DAY', labelExpression: 'schedulestep.ScheduleEventEditFlexible.DayOptions.AnyDay'},
    {id: 'Sun', labelExpression: 'schedulestep.ScheduleEventEditFlexible.DayOptions.Sun'},
    {id: 'Mon', labelExpression: 'schedulestep.ScheduleEventEditFlexible.DayOptions.Mon'},
    {id: 'Tue', labelExpression: 'schedulestep.ScheduleEventEditFlexible.DayOptions.Tue'},
    {id: 'Wed', labelExpression: 'schedulestep.ScheduleEventEditFlexible.DayOptions.Wed'},
    {id: 'Thu', labelExpression: 'schedulestep.ScheduleEventEditFlexible.DayOptions.Thu'},
    {id: 'Fri', labelExpression: 'schedulestep.ScheduleEventEditFlexible.DayOptions.Fri'},
    {id: 'Sat', labelExpression: 'schedulestep.ScheduleEventEditFlexible.DayOptions.Sat'},
  ]), []);
  const timeOptions = React.useMemo(() => ([
    {id: 'ANY_TIME', labelExpression: 'schedulestep.ScheduleEventEditFlexible.TimeOptions.AnyTime'},
    {id: 'MORNING', labelExpression: 'schedulestep.ScheduleEventEditFlexible.TimeOptions.Morning'},
    {id: 'AFTERNOON', labelExpression: 'schedulestep.ScheduleEventEditFlexible.TimeOptions.Afternoon'},
    {id: 'EVENING', labelExpression: 'schedulestep.ScheduleEventEditFlexible.TimeOptions.Evening'},
    {id: 'OVERNIGHT', labelExpression: 'schedulestep.ScheduleEventEditFlexible.TimeOptions.Overnight'},
  ]), []);

  const sortRepeats = React.useCallback((repeatDays = []) => {
    return [...repeatDays].sort((dayA, dayB) => daysOfWeek.indexOf(dayA) - daysOfWeek.indexOf(dayB));
  }, [daysOfWeek]);

  const resetFromInitialEvent = React.useCallback(() => {
    if (!initialEvent) {
      setPreferredDays([]);
      setPreferredTimes([]);
      setVisitDuration('');
      return;
    }

    setPreferredDays([...initialEvent.preferredDays]);
    setPreferredTimes([...initialEvent.preferredTimes]);
    setVisitDuration(`${initialEvent.visitDuration}`);
  }, [initialEvent]);

  React.useEffect(() => {
    resetFromInitialEvent();
  }, [resetFromInitialEvent]);

  const handleDayToggle = React.useCallback((dayId) => {
    if (dayId === 'ANY_DAY') {
      setPreferredDays(['ANY_DAY']);
      return;
    }

    setPreferredDays((previousPreferredDays) => {
      const withoutAnyDay = previousPreferredDays.filter((item) => item !== 'ANY_DAY');
      if (withoutAnyDay.includes(dayId)) {
        return withoutAnyDay.filter((item) => item !== dayId);
      }

      return [...withoutAnyDay, dayId];
    });
  }, []);

  const handleTimeToggle = React.useCallback((timeId) => {
    if (timeId === 'ANY_TIME') {
      setPreferredTimes(['ANY_TIME']);
      return;
    }

    setPreferredTimes((previousPreferredTimes) => {
      const withoutAnyTime = previousPreferredTimes.filter((item) => item !== 'ANY_TIME');
      if (withoutAnyTime.includes(timeId)) {
        return withoutAnyTime.filter((item) => item !== timeId);
      }

      return [...withoutAnyTime, timeId];
    });
  }, []);

  const handleSave = React.useCallback(() => {
    const normalizedVisitDuration = `${visitDuration}`.trim();
    if (!normalizedVisitDuration) {
      return;
    }

    const nextEvent = {
      scheduleType: 'FLEXIBLE',
      preferredDays: sortRepeats(preferredDays),
      preferredTimes,
      visitDuration: normalizedVisitDuration,
    };

    onSave(nextEvent);
  }, [onSave, preferredDays, preferredTimes, sortRepeats, visitDuration]);

  const canSave = Boolean(`${visitDuration}`.trim());

  return (
    <Box
      sx={{
        border: '1px dashed #C5BBB9',
        borderRadius: '24px',
        p: '32px',
        backgroundColor: 'white',
      }}
    >
      <Stack sx={{gap: '32px'}}>
        <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'space-between'}}>
          <Box sx={{display: 'flex', alignItems: 'center', gap: '12px'}}>
            <IconButton
              onClick={onRemove}
              aria-label={translate('schedulestep.ScheduleEventEditFlexible.RemoveAriaLabel')}
              sx={{
                'border': '1px solid #D4C8C2',
                'borderRadius': '8px',
                'color': '#5E5855',
                'p': '6px',
                '&:hover': {
                  borderColor: '#BDAFA8',
                  backgroundColor: '#F7F3F0',
                },
              }}
            >
              <ChevronLeft sx={{fontSize: 24}} />
            </IconButton>
            <Typography sx={{fontSize: '20px', fontWeight: 300, color: '#2D2925', lineHeight: '128%', fontFamily: '"Spectral", serif'}}>
              <Translate expression={'schedulestep.ScheduleEventEditFlexible.Title'} />
            </Typography>
          </Box>
          <Box sx={{border: '1px solid #C5BBB9', borderRadius: '22px', px: '16px', py: '8px'}}>
            <Typography sx={{fontSize: '14px', fontWeight: 400, color: '#3C3C3C', lineHeight: '128%', whiteSpace: 'nowrap'}}>
              <Translate expression={'schedulestep.ScheduleEventEditFlexible.StepLabel'} />
            </Typography>
          </Box>
        </Box>

        <Box sx={{gap: '16px', display: 'flex', flexDirection: 'column'}}>
          <Box>
            <Typography sx={{fontSize: '16px', fontWeight: 590, color: '#2D2925', lineHeight: '150%', mb: '10px'}}>
              <Translate expression={'schedulestep.ScheduleEventEditFlexible.PreferredDayOptional'} />
            </Typography>
            <Box sx={{display: 'flex', gap: '8px', flexWrap: 'wrap'}}>
              {
                dayOptions.map((dayOption) => {
                  const isSelected = preferredDays.includes(dayOption.id);
                  return (
                    <Button
                      key={dayOption.id}
                      onClick={() => handleDayToggle(dayOption.id)}
                      variant='outlined'
                      sx={{
                        'minWidth': 0,
                        'borderRadius': '8px',
                        'px': '14px',
                        'py': '8px',
                        'fontSize': '16px',
                        'fontWeight': 400,
                        'lineHeight': '150%',
                        'textTransform': 'none',
                        'color': '#2D2925',
                        'borderColor': isSelected ? '#DA7955' : '#C5BBB9',
                        'borderStyle': isSelected ? 'solid' : 'dashed',
                        'backgroundColor': isSelected ? '#FFFDFC' : '#0000000F',
                        '&:hover': {
                          borderColor: '#DA7955',
                          backgroundColor: isSelected ? '#FFF2EB' : '#EFE8E2',
                        },
                      }}
                    >
                      <Translate expression={dayOption.labelExpression} />
                    </Button>
                  );
                })
              }
            </Box>
          </Box>

          <Box>
            <Typography sx={{fontSize: '16px', fontWeight: 590, color: '#2D2925', lineHeight: '150%', mb: '10px'}}>
              <Translate expression={'schedulestep.ScheduleEventEditFlexible.PreferredTimeOfDayOptional'} />
            </Typography>
            <Box sx={{display: 'flex', gap: '8px', flexWrap: 'wrap'}}>
              {
                timeOptions.map((timeOption) => {
                  const isSelected = preferredTimes.includes(timeOption.id);
                  return (
                    <Button
                      key={timeOption.id}
                      onClick={() => handleTimeToggle(timeOption.id)}
                      variant='outlined'
                      sx={{
                        'minWidth': 0,
                        'borderRadius': '8px',
                        'px': '14px',
                        'py': '8px',
                        'fontSize': '16px',
                        'fontWeight': 400,
                        'lineHeight': '150%',
                        'textTransform': 'none',
                        'color': '#2D2925',
                        'borderColor': isSelected ? '#DA7955' : '#C5BBB9',
                        'borderStyle': isSelected ? 'solid' : 'dashed',
                        'backgroundColor': isSelected ? '#FFFDFC' : '#0000000F',
                        '&:hover': {
                          borderColor: '#DA7955',
                          backgroundColor: isSelected ? '#FFF2EB' : '#EFE8E2',
                        },
                      }}
                    >
                      <Translate expression={timeOption.labelExpression} />
                    </Button>
                  );
                })
              }
            </Box>
          </Box>

          <Box sx={{minWidth: 0}}>
            <Typography sx={{fontSize: '16px', fontWeight: 590, color: '#2D2925', lineHeight: '150%', mb: '8px'}}>
              <Translate expression={'schedulestep.ScheduleEventEditFlexible.VisitDurationNeeded'} />
            </Typography>
            <TextField
              fullWidth
              value={visitDuration}
              placeholder={translate('schedulestep.ScheduleEventEditFlexible.VisitDurationPlaceholder')}
              onChange={(value) => setVisitDuration(value)}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '12px',
                },
                '& .MuiOutlinedInput-input': {
                  fontSize: '16px',
                  fontWeight: 400,
                  lineHeight: '150%',
                  py: '14px',
                  px: '12px',
                },
                '& .MuiOutlinedInput-input::placeholder': {
                  color: '#A69E9A',
                  opacity: 1,
                },
              }}
            />
          </Box>


          <Box sx={{p: '24px', borderRadius: '16px', backgroundColor: '#DA795514'}}>
            <Box sx={{display: 'flex', alignItems: 'center', mb: '12px', gap: '8px'}}>
              <InfoOutlined sx={{fontSize: 20, color: '#DA7955'}} />
              <Typography sx={{fontSize: '16px', fontWeight: 590, color: '#2D2925', lineHeight: '150%'}}>
                <Translate expression={'schedulestep.Common.PleaseNote'} />
              </Typography>
            </Box>
            <Typography sx={{fontSize: '16px', fontWeight: 400, color: '#2D2925', lineHeight: '150%'}}>
              <Translate expression={'schedulestep.ScheduleEventEditFlexible.ShortShiftNotice'} />
            </Typography>
          </Box>
        </Box>

        <Box sx={{display: 'flex', justifyContent: 'flex-end', gap: '12px', flexWrap: 'wrap'}}>
          <Button
            onClick={handleSave}
            variant='contained'
            endIcon={<ArrowForwardIos />}
            disabled={!canSave}
            sx={{
              'borderRadius': '12px',
              'textTransform': 'none',
              'backgroundColor': '#DA7955',
              'fontSize': '16px',
              'fontWeight': 500,
              'lineHeight': '150%',
              'color': '#F6F2EF',
              'px': '20px',
              'py': '14px',
              'gap': '4px',
              '&:hover': {backgroundColor: '#C86945'},
            }}
          >
            <Translate expression={'schedulestep.ScheduleEventEditFlexible.SaveVisitButton'} />
          </Button>
        </Box>
      </Stack>
    </Box>
  );
};

ScheduleEventEditFlexible.propTypes = {
  initialEvent: PropTypes.shape({
    preferredDays: PropTypes.arrayOf(PropTypes.string),
    preferredTimes: PropTypes.arrayOf(PropTypes.string),
    visitDuration: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  }),
  onSave: PropTypes.func,
  onRemove: PropTypes.func,
};

export default React.memo(ScheduleEventEditFlexible);
